<script setup lang="ts">
    import { DatePicker } from 'v-calendar';
    import 'v-calendar/style.css';
    import { ref, watch } from 'vue';
    import { CalendarIcon } from '@lucide/vue';
    import { __ } from '../stores/expressions';
    import { onMounted, onBeforeUnmount } from 'vue';
    import { TextInputWidth } from './TextInput.vue';
    import { dateToISO } from '../utils/dateUtils';

    const parseDateString = (value: string): Date => {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    };

    const validDate = function (date: string): boolean {
        if (String(date).length !== 10 || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(String(date))) {
            return false;
        }

        const [year, month, day] = date.split('-').map(Number);
        const parsed = new Date(year, month - 1, day);

        return (
            parsed.getFullYear() === year &&
            parsed.getMonth() === month - 1 &&
            parsed.getDate() === day
        );
    };

    const format = function (event: KeyboardEvent): string {
        const target = event.target as HTMLInputElement;
        const key = event.key;

        if (/^\d$/.test(key)) {
            if (target.value.length === 4) {
                target.value += '-';
            } else if (target.value.length === 7) {
                target.value += '-';
            }
        } else if (key === 'Backspace') {
            if (target.value.length === 6 || target.value.length === 9) {
                target.value = target.value.slice(0, -1);
            }
        }

        return target.value;
    };

    const emit = defineEmits<{
        (e: 'update:modelValue', value: string): void;
    }>();

    const props = withDefaults(
        defineProps<{
            modelValue: string;
            id?: string;
            placeholder?: string;
            placement?: 'top' | 'bottom' | 'auto';
        }>(),
        {
            id: undefined,
            placeholder: undefined,
            placement: 'auto',
        }
    );

    const resolvedPlacement = ref<'top' | 'bottom'>('bottom');

    const calendar = ref<InstanceType<typeof DatePicker> | null>(null);
    const rootEl = ref<HTMLElement | null>(null);

    const picked_date = ref<Date | null>(
        validDate(props.modelValue) ? parseDateString(props.modelValue) : null
    );

    const selected = ref(validDate(props.modelValue) ? props.modelValue : '');

    const active = ref(false);
    let activeTimeout: number | undefined;

    const updatePlacement = () => {
        if (props.placement !== 'auto' || !rootEl.value) {
            resolvedPlacement.value = props.placement === 'top' ? 'top' : 'bottom';
            return;
        }
        const rect = rootEl.value.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const calendarHeight = 310;
        resolvedPlacement.value = spaceBelow < calendarHeight ? 'top' : 'bottom';
    };

    const activate = () => {
        clearTimeout(activeTimeout);
        updatePlacement();
        active.value = true;
    };

    const inactivate = () => {
        clearTimeout(activeTimeout);
        activeTimeout = window.setTimeout(() => {
            active.value = false;
        }, 200);
    };
    const handleClickOutside = (event: MouseEvent) => {
        if (rootEl.value && !rootEl.value.contains(event.target as Node)) {
            inactivate();
        }
    };

    const handleFocusOut = (event: FocusEvent) => {
        const nextFocus = event.relatedTarget as Node | null;
        if (!rootEl.value || !nextFocus || !rootEl.value.contains(nextFocus)) {
            inactivate();
        }
    };

    onMounted(() => {
        document.addEventListener('mousedown', handleClickOutside);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('mousedown', handleClickOutside);
    });

    watch(
        () => props.modelValue,
        (newValue) => {
            if (!newValue) {
                selected.value = '';
                picked_date.value = null;
                return;
            }

            if (validDate(newValue)) {
                selected.value = newValue;
                picked_date.value = parseDateString(newValue);
            }
        }
    );

    watch(picked_date, (newValue, oldValue) => {
        if (!newValue) {
            return;
        }
        const formattedDate = dateToISO(newValue);

        if (validDate(formattedDate)) {
            selected.value = formattedDate;
        }

        if (oldValue) {
            const oldFormattedDate = dateToISO(oldValue);
            if (oldFormattedDate !== formattedDate) {
                active.value = false;
            }
        }
    });

    watch(
        () => selected.value,
        (newValue) => {
            if (!newValue) {
                if (props.modelValue !== '') {
                    emit('update:modelValue', '');
                }
                return;
            }
            if (validDate(newValue)) {
                const parsed = parseDateString(newValue);

                if (!picked_date.value || dateToISO(picked_date.value) !== newValue) {
                    picked_date.value = parsed;
                }

                emit('update:modelValue', newValue);

                if (calendar.value) {
                    calendar.value.move(parsed);
                }
            }
        }
    );
</script>

<template>
    <div ref="rootEl" class="relative mb-5" @focusout="handleFocusOut">
        <div class="relative">
            <CalendarIcon class="size-5 absolute top-2.25 left-3 pointer-events-none" />
            <input
                :id="id"
                v-model="selected"
                type="tel"
                :placeholder="placeholder ?? __('YYYY-MM-DD')"
                class="TextInput pl-10"
                :class="TextInputWidth.Full"
                @keydown="selected = format($event)"
                @focus="activate()"
            />
        </div>
        <div
            class="absolute overflow-hidden z-1"
            :class="[
                active ? 'h-auto' : 'pointer-events-none h-0',
                resolvedPlacement === 'top' ? 'bottom-full mb-2' : 'mt-2',
            ]"
        >
            <DatePicker
                ref="calendar"
                v-model="picked_date"
                class="vcalendar-input"
                mode="date"
                :first-day-of-week="2"
                @focus="activate()"
            />
        </div>
    </div>
</template>
